(function () {
  'use strict';

  var SECTION_SELECTOR = '#gc-collection-grid';
  var DEBOUNCE_MS = 600;
  var VIEW_KEY = 'gc-view-mode';

  var section, sectionId;

  function init() {
    section = document.querySelector(SECTION_SELECTOR);
    if (!section) return;
    sectionId = section.dataset.sectionId;

    bindAll();
    bindEscapeKey();
    bindTitleFixResize();
    restoreViewMode();
    initTitleFix();

    window.addEventListener('popstate', function () {
      renderSection(window.location.href);
    });
  }

  function bindAll() {
    bindSort();
    bindFilterCheckboxes();
    bindFilterLinks();
    bindPagination();
    bindPriceSliders();
    bindViewSwitcher();
    bindMobileDrawer();
  }

  // ——— Section Rendering API ———

  function renderSection(url) {
    section.classList.add('is-loading');

    var fetchUrl = new URL(url, window.location.origin);
    fetchUrl.searchParams.set('section_id', sectionId);

    fetch(fetchUrl.toString())
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var newSection = doc.querySelector(SECTION_SELECTOR);
        section.classList.remove('is-loading');

        // Only rebind when we actually replaced the DOM with fresh nodes.
        // If newSection wasn't found, the existing (already-bound) nodes are
        // untouched — rebinding here would stack duplicate listeners on them.
        if (newSection) {
          section.innerHTML = newSection.innerHTML;
          bindAll();
          restoreViewMode();
          fixTitleLines();
        }
      })
      .catch(function () {
        section.classList.remove('is-loading');
      });
  }

  function navigateAndRender(url) {
    window.history.pushState({}, '', url);
    renderSection(url);
  }

  // ——— Sort ———

  function bindSort() {
    var sel = section.querySelector('[data-gc-sort-select]');
    if (!sel) return;
    sel.addEventListener('change', handleSort);
  }

  function handleSort(e) {
    var url = new URL(window.location.href);
    url.searchParams.set('sort_by', e.target.value);
    url.searchParams.delete('page');
    navigateAndRender(url.toString());
  }

  // ——— Filter checkboxes (boolean + list types) ———

  function bindFilterCheckboxes() {
    var checks = section.querySelectorAll('[data-gc-filter-url-apply]');
    for (var i = 0; i < checks.length; i++) {
      checks[i].addEventListener('change', handleFilterCheck);
    }
  }

  function handleFilterCheck(e) {
    var url = e.target.getAttribute('data-gc-filter-url-apply');
    if (url) navigateAndRender(url);
  }

  // ——— Filter chip / clear links ———

  function bindFilterLinks() {
    var links = section.querySelectorAll('[data-gc-filter-link]');
    for (var i = 0; i < links.length; i++) {
      links[i].addEventListener('click', handleFilterLink);
    }
  }

  function handleFilterLink(e) {
    e.preventDefault();
    var url = e.currentTarget.getAttribute('href');
    if (url) navigateAndRender(url);
  }

  // ——— Pagination ———

  function bindPagination() {
    var links = section.querySelectorAll('[data-gc-page]');
    for (var i = 0; i < links.length; i++) {
      links[i].addEventListener('click', handlePageLink);
    }
  }

  function handlePageLink(e) {
    e.preventDefault();
    var url = e.currentTarget.getAttribute('href');
    if (url) {
      navigateAndRender(url);
      var hero = document.querySelector('.gc-hero') || document.querySelector('.gc-controls');
      if (hero) hero.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  // ——— Price range slider ———

  var priceTimers = [];

  function bindPriceSliders() {
    var widgets = section.querySelectorAll('[data-gc-price-filter]');
    for (var w = 0; w < widgets.length; w++) {
      setupPriceWidget(widgets[w], w);
    }
  }

  function setupPriceWidget(widget, idx) {
    var handleMin = widget.querySelector('[data-price-handle-min]');
    var handleMax = widget.querySelector('[data-price-handle-max]');
    var inputMin = widget.querySelector('[data-price-input-min]');
    var inputMax = widget.querySelector('[data-price-input-max]');
    var rangeBar = widget.querySelector('.gc-price-filter__range');
    var rangeMax = parseInt(widget.dataset.rangeMax, 10);

    if (!handleMin || !handleMax || !inputMin || !inputMax) return;

    function syncUI() {
      var lo = parseInt(handleMin.value, 10);
      var hi = parseInt(handleMax.value, 10);
      if (lo > hi) { var tmp = lo; lo = hi; hi = tmp; }
      inputMin.value = lo;
      inputMax.value = hi;
      if (rangeBar && rangeMax > 0) {
        rangeBar.style.left = (lo / rangeMax * 100) + '%';
        rangeBar.style.right = (100 - hi / rangeMax * 100) + '%';
      }
    }

    function debouncedApply() {
      clearTimeout(priceTimers[idx]);
      priceTimers[idx] = setTimeout(function () {
        applyPriceFilter(widget);
      }, DEBOUNCE_MS);
    }

    handleMin.addEventListener('input', function () { syncUI(); debouncedApply(); });
    handleMax.addEventListener('input', function () { syncUI(); debouncedApply(); });

    inputMin.addEventListener('change', function () {
      handleMin.value = inputMin.value;
      syncUI();
      debouncedApply();
    });

    inputMax.addEventListener('change', function () {
      handleMax.value = inputMax.value;
      syncUI();
      debouncedApply();
    });
  }

  function applyPriceFilter(widget) {
    var paramMin = widget.dataset.paramMin;
    var paramMax = widget.dataset.paramMax;
    var lo = parseInt(widget.querySelector('[data-price-input-min]').value, 10) || 0;
    var hi = parseInt(widget.querySelector('[data-price-input-max]').value, 10) || parseInt(widget.dataset.rangeMax, 10);
    var rangeMax = parseInt(widget.dataset.rangeMax, 10);

    var url = new URL(window.location.href);
    url.searchParams.delete('page');

    // Shopify's filter.v.price.gte/.lte params expect major currency units
    // (e.g. rupees), matching what's typed in the input — NOT the subunit
    // (paise) integers filter.min_value.value/range_max return in Liquid.
    // Verified live: lte=3000 correctly excludes a ₹4,000 item; lte=300000
    // (the ×100'd version) does not filter at all.
    if (lo > 0) {
      url.searchParams.set(paramMin, lo);
    } else {
      url.searchParams.delete(paramMin);
    }

    if (hi < rangeMax) {
      url.searchParams.set(paramMax, hi);
    } else {
      url.searchParams.delete(paramMax);
    }

    navigateAndRender(url.toString());
  }

  // ——— View mode switcher ———

  function bindViewSwitcher() {
    var btns = section.querySelectorAll('[data-view-mode]');
    for (var i = 0; i < btns.length; i++) {
      btns[i].addEventListener('click', handleViewMode);
    }
  }

  function handleViewMode(e) {
    setViewMode(e.currentTarget.dataset.viewMode);
  }

  function setViewMode(mode) {
    var grid = section.querySelector('[data-gc-grid]');
    if (grid) grid.setAttribute('data-view', mode);

    var btns = section.querySelectorAll('[data-view-mode]');
    for (var i = 0; i < btns.length; i++) {
      btns[i].setAttribute('aria-pressed', btns[i].dataset.viewMode === mode ? 'true' : 'false');
    }

    try { localStorage.setItem(VIEW_KEY, mode); } catch (e) {}
  }

  function restoreViewMode() {
    try {
      var saved = localStorage.getItem(VIEW_KEY);
      if (saved) setViewMode(saved);
    } catch (e) {}
  }

  // ——— Mobile filter drawer ———

  function bindMobileDrawer() {
    var openBtns = section.querySelectorAll('[data-gc-filter-open]');
    var closeBtns = section.querySelectorAll('[data-gc-filter-close]');
    var overlay = section.querySelector('[data-gc-filter-overlay]');
    var drawer = section.querySelector('[data-gc-filter-drawer]');

    function open() {
      if (!drawer || !overlay) return;
      overlay.hidden = false;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    function close() {
      if (!drawer || !overlay) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      overlay.hidden = true;
      document.body.style.overflow = '';
    }

    for (var i = 0; i < openBtns.length; i++) openBtns[i].addEventListener('click', open);
    for (var j = 0; j < closeBtns.length; j++) closeBtns[j].addEventListener('click', close);
    if (overlay) overlay.addEventListener('click', close);
  }

  // Bound once on document (never replaced by AJAX), so this must live
  // outside bindMobileDrawer() to avoid stacking duplicate listeners on
  // every re-render. Queries section's current children fresh each time,
  // since `section` itself is stable but its innerHTML is swapped on AJAX.
  function bindEscapeKey() {
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var drawer = section.querySelector('[data-gc-filter-drawer]');
      var overlay = section.querySelector('[data-gc-filter-overlay]');
      if (!drawer || !overlay || !drawer.classList.contains('is-open')) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      overlay.hidden = true;
      document.body.style.overflow = '';
    });
  }

  // ——— Title 2-line alignment ———
  // Add to Bag / Quick View must start at the same Y position across every
  // card in a row, which means every card's title box must be exactly the
  // same height. CSS -webkit-line-clamp (see collection-grid.liquid) already
  // truncates a naturally-3+-line title down to 2 lines with an ellipsis,
  // but does nothing for a title that's naturally only 1 line — that box
  // just stays 1 line tall. This measures each title's true, unclamped line
  // count and, only for genuine 1-liners, forces a break before the last
  // word so the box is always 2 real lines of text. A 2-line title is left
  // completely untouched.

  var TITLE_MEASURE_CLASS = 'gc-card__name--measuring';
  var titleResizeTimer;

  function getLineHeightPx(el) {
    var lh = parseFloat(getComputedStyle(el).lineHeight);
    return isNaN(lh) || lh <= 0 ? 0 : lh;
  }

  function fixOneTitle(el) {
    // Always resolve to the untouched original text before re-measuring.
    // On a resize this undoes a break forced at a previous (now stale)
    // card width; on a freshly AJAX-rendered node there's nothing to
    // undo yet. A <br> contributes no whitespace to textContent, so a
    // previously-forced title's textContent won't match its stored
    // original — that's the signal a reset is needed.
    var full = el.dataset.gcTitleFull;
    if (full == null) {
      full = el.textContent.trim();
      el.dataset.gcTitleFull = full;
    } else if (el.textContent.trim() !== full) {
      el.textContent = full;
    }

    el.classList.add(TITLE_MEASURE_CLASS);
    var lineHeight = getLineHeightPx(el);
    var naturalLines = lineHeight ? Math.round(el.getBoundingClientRect().height / lineHeight) : 1;
    el.classList.remove(TITLE_MEASURE_CLASS);

    // 2 lines: already correct, leave as-is. 3+ lines: leave the plain
    // text in place and let -webkit-line-clamp truncate it with an
    // ellipsis — no JS truncation needed. Only a genuine 1-liner needs
    // the forced break.
    if (naturalLines !== 1) return;

    var lastSpace = full.lastIndexOf(' ');
    if (lastSpace === -1) return; // single word — nothing to break before

    el.textContent = '';
    el.appendChild(document.createTextNode(full.slice(0, lastSpace)));
    el.appendChild(document.createElement('br'));
    el.appendChild(document.createTextNode(full.slice(lastSpace + 1)));
  }

  function fixTitleLines() {
    if (!section) return;
    var names = section.querySelectorAll('.gc-card__name');
    for (var i = 0; i < names.length; i++) {
      fixOneTitle(names[i]);
    }
  }

  // Bound once on window (never replaced by AJAX), so this must live
  // outside bindAll() to avoid stacking duplicate listeners on every
  // re-render — same reasoning as bindEscapeKey() above. Re-measures
  // whatever cards currently exist in the section, so it stays correct
  // across a breakpoint/column-count change and after AJAX swaps.
  function bindTitleFixResize() {
    window.addEventListener('resize', function () {
      clearTimeout(titleResizeTimer);
      titleResizeTimer = setTimeout(fixTitleLines, 150);
    });
  }

  // Measuring before webfonts land would use fallback-font metrics and can
  // misjudge a title's true line count, so the first run waits for them.
  function initTitleFix() {
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(fixTitleLines).catch(fixTitleLines);
    } else {
      fixTitleLines();
    }
  }

  // ——— Boot ———

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
